import { BaseExportService } from "./BaseExportService";

export class AMLExportService extends BaseExportService {
  constructor(data, metadata = {}) {
    super(data, metadata);

    // Extract AML data structure
    this.aml_data = this.extract_aml_data();
    this.response_info = this.extract_response_info();
    this.request_data = this.extract_request_data();
    this.watchlist_summary = this.extract_watchlist_summary();
    this.verification_status = this.extract_verification_status();
  }

  /**
   * Safely get the root result object, handling both
   */
  get_root() {
    return this.data?.result || this.data || {};
  }

  /**
   * Get the first applicant record from the response.
   */
  get_first_applicant() {
    const root = this.get_root();
    return root?.Fields?.Applicants_IO?.Applicant?.[0] || {};
  }

  /**
   * Extract DSWatchlistVerification data (NmResults, PepResults).
   */
  extract_aml_data() {
    const applicant = this.get_first_applicant();
    return applicant?.Attributes?.DSWatchlistVerification || {};
  }

  /**
   * Extract DRWatchlistVerification summary (MatchCount, Decision, etc.).
   */
  extract_watchlist_summary() {
    const applicant = this.get_first_applicant();
    return applicant?.Attributes?.DRWatchlistVerification || {};
  }

  /**
   * Extract DSWatchlistVerificationStatus.
   */
  extract_verification_status() {
    const applicant = this.get_first_applicant();
    return applicant?.DSWatchlistVerificationStatus || {};
  }

  /**
   * Extract response info (ApplicationId, SolutionSetInstanceId, etc.).
   */
  extract_response_info() {
    const root = this.get_root();
    return root?.ResponseInfo || this.metadata?.response_info || {};
  }

  /**
   * Extract request data (FirstName, LastName, etc.).
   */
  extract_request_data() {
    const root = this.get_root();
    return root?.RequestData || this.metadata?.request_data || {};
  }

  get_filename_prefix() {
    const app_id = this.response_info?.ApplicationId || "Unknown";
    return `AML_Report_${app_id}`;
  }

  get_available_sections() {
    return {
      subject_information: "Subject Information",
      screening_summary: "Screening Summary",
      negative_media: "Negative Media Results",
      pep: "Politically Exposed Persons (PEP)",
      watchlist: "Watchlist Results",
      raw_json: "Raw JSON Data",
    };
  }

  get_default_export_options() {
    return {
      subject_information: false,
      screening_summary: false,
      negative_media: false,
      pep: false,
      watchlist: false,
      raw_json: false,
    };
  }

  prepare_export_data(export_options) {
    const export_data = {};

    if (export_options.subject_information) {
      export_data.subject_information = this.prepare_subject_data();
    }

    if (export_options.screening_summary) {
      export_data.screening_summary = this.prepare_screening_summary();
    }

    if (export_options.negative_media) {
      export_data.negative_media = this.prepare_negative_media_data();
    }

    if (export_options.pep) {
      export_data.pep = this.prepare_pep_data();
    }

    if (export_options.watchlist) {
      export_data.watchlist = this.prepare_watchlist_data();
    }

    if (export_options.raw_json) {
      export_data.raw_json = [this.prepare_raw_json_data()];
    }

    return export_data;
  }

  // ──────────────────────────────────────────────
  // Helpers for formatting nested / mixed arrays
  // ──────────────────────────────────────────────

  /**
   * Format an array that may contain plain strings OR
   * objects like { relationType, name }.
   */
  format_relation_array(arr) {
    if (!Array.isArray(arr) || arr.length === 0) return "N/A";
    return arr
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") {
          // Handle { relationType, name } objects
          const relation = item.relationType || "";
          const name = item.name || "";
          return relation ? `${relation}: ${name}` : name;
        }
        return String(item);
      })
      .filter(Boolean)
      .join("; ");
  }

  /**
   * Format a plain string array (concepts, highlights, etc.).
   */
  format_string_array(arr) {
    if (!Array.isArray(arr) || arr.length === 0) return "N/A";
    return arr.join("; ");
  }

  /**
   * Normalise "None" strings to N/A for display.
   */
  safe_val(val) {
    if (val === null || val === undefined || val === "None") return "N/A";
    return val;
  }

  // ──────────────────────────────────────────────
  // Section preparers
  // ──────────────────────────────────────────────

  /**
   * Prepare subject information for export
   */
  prepare_subject_data() {
    // Handle the API typo: DateOfDirth vs DateOfBirth
    const dob =
      this.request_data?.DateOfBirth || this.request_data?.DateOfDirth || "N/A";
    const formatted_dob = this.format_date_string(dob);

    return [
      {
        "Application ID": this.safe_val(this.response_info?.ApplicationId),
        "Solution Set Instance ID": this.safe_val(
          this.response_info?.SolutionSetInstanceId,
        ),
        "Current Queue": this.safe_val(this.response_info?.CurrentQueue),
        "First Name": this.safe_val(this.request_data?.FirstName),
        "Last Name": this.safe_val(this.request_data?.LastName),
        "Date of Birth": formatted_dob,
        Gender: this.safe_val(this.request_data?.Gender),
        "Country Code": this.safe_val(this.request_data?.CountryCode),
        "Verification Status": this.safe_val(this.verification_status?.Outcome),
        "Verification Success": this.verification_status?.IsSuccess ?? "N/A",
        "Total NM Results": this.aml_data?.NmResults?.length || 0,
        "Total PEP Results": this.aml_data?.PepResults?.length || 0,
        "Total Watchlist Results": this.aml_data?.WlsResults?.length || 0,
      },
    ];
  }

  /**
   * Prepare screening summary (DRWatchlistVerification).
   */
  prepare_screening_summary() {
    return [
      {
        "Watchlist Decision": this.safe_val(
          this.watchlist_summary?.WatchlistDecision,
        ),
        "Match Count": this.watchlist_summary?.MatchCount ?? "N/A",
        "API Response Time (ms)": this.safe_val(
          this.watchlist_summary?.APIResponseTime,
        ),
        "Time of Request": this.safe_val(this.watchlist_summary?.TimeOfRequest),
        "Service Time (ms)": this.watchlist_summary?.ServiceTime ?? "N/A",
        "Response Size (bytes)": this.watchlist_summary?.ResponseSize ?? "N/A",
        "Verification Outcome": this.safe_val(
          this.verification_status?.Outcome,
        ),
        "Verification Success": this.verification_status?.IsSuccess ?? "N/A",
      },
    ];
  }

  /**
   * Prepare Negative Media results for export
   */
  prepare_negative_media_data() {
    const nm_results = this.aml_data?.NmResults || [];

    if (nm_results.length === 0) {
      return [{ Info: "No negative media results found." }];
    }

    return nm_results.map((result, index) => ({
      "Result #": index + 1,
      "Product ID": this.safe_val(result.productId),
      Score: result.score ?? "N/A",
      "Source ID": this.safe_val(result.sourceId),
      Category: this.safe_val(result.category),
      "Result ID": this.safe_val(result.resultId),
      "Source Country": this.safe_val(result.sourceCountry),
      "Subject Matched": this.safe_val(result.subjectMatched),
      "Subject Age": this.safe_val(result.subjectAge),
      "Subject Location": this.safe_val(result.subjectLocation),
      "Article Title": this.safe_val(result.articleTitle),
      "Article Date": this.safe_val(result.articleDate),
      Text: this.safe_val(this.strip_html(result.text)),
      URL: this.safe_val(result.url),
      Highlights: this.format_string_array(result.highlights),
      Concepts: this.format_string_array(result.concepts),
    }));
  }

  /**
   * Prepare PEP results for export
   */
  prepare_pep_data() {
    const pep_results = this.aml_data?.PepResults || [];

    if (pep_results.length === 0) {
      return [{ Info: "No PEP results found." }];
    }

    return pep_results.map((result, index) => ({
      "Result #": index + 1,
      "Product ID": this.safe_val(result.productId),
      Score: result.score ?? "N/A",
      "Profile ID": this.safe_val(result.profileId),
      "Result ID": this.safe_val(result.resultId),
      Name: this.safe_val(result.name),
      "Birth Name": this.safe_val(result.birthName),
      "Native Name": this.safe_val(result.nativeName),
      Country: this.safe_val(result.country),
      "Country Code": this.safe_val(result.countryCode),
      Gender: this.safe_val(result.gender),
      "Date of Birth": this.safe_val(result.dateOfBirth),
      "Date of Death": this.safe_val(result.dateOfDeath),
      "Place of Birth": this.safe_val(result.placeOfBirth),
      "PEP Search Type": this.safe_val(result.pepSearchType),
      "Image URL": this.safe_val(result.imageURL),
      "Professional History": this.format_string_array(
        result.professionalHistory,
      ),
      "Member Of": this.format_string_array(result.memberOf),
      Employer: this.format_string_array(result.employer),
      Education: this.format_string_array(result.education),
      "Awards Received": this.format_string_array(result.awardsReceived),
      "Notable Work": this.format_string_array(result.notableWork),
      Convicted: this.format_string_array(result.convicted),
      "Source Info URL": this.format_string_array(result.sourceInfoURL),
      Address: this.format_string_array(result.address),
      Affiliation: this.format_string_array(result.affiliation),
      Aliases: this.format_string_array(result.aliases),
      "Family Members": this.format_relation_array(result.familyMembers),
      Social: this.format_relation_array(result.social),
      "Business Relations": this.format_relation_array(
        result.businessRelations,
      ),
      "Business Partnerships": this.format_relation_array(
        result.businessPartnerships,
      ),
      Lobbying: this.format_relation_array(result.lobbying),
      Stakeholder: this.format_relation_array(result.stakeholder),
      Contributions: this.format_relation_array(result.contributions),
      Tags: this.format_string_array(result.tags),
      "Score Boosts": this.format_string_array(result.scoreBoosts),
    }));
  }

  /**
   * Prepare Watchlist results for export
   */
  prepare_watchlist_data() {
    const wls_results = this.aml_data?.WlsResults || [];

    if (wls_results.length === 0) {
      return [{ Info: "No watchlist results found." }];
    }

    return wls_results.map((result, index) => ({
      "Result #": index + 1,
      "Product ID": this.safe_val(result.productId),
      Score: result.score ?? "N/A",
      "Source ID": this.safe_val(result.sourceId),
      Category: this.safe_val(result.category),
      "Result ID": this.safe_val(result.resultId),
      "Source Country": this.safe_val(result.sourceCountry),
      "Subject Matched": this.safe_val(result.subjectMatched),
      Text: this.safe_val(this.strip_html(result.text)),
      URL: this.safe_val(result.url),
      Highlights: this.format_string_array(result.highlights),
      Address: this.safe_val(result.address),
      Addresses: this.safe_val(result.addresses),
      Age: this.safe_val(result.age),
      "Alias List": this.safe_val(result.aliasList),
      Caution: this.safe_val(result.caution),
      "Date of Birth": this.safe_val(result.dateOfBirth),
      "Dates of Birth Normalized": this.safe_val(result.datesOfBirthNormalized),
      "Birth Years": this.safe_val(result.birthYears),
      "Entity Name": this.safe_val(result.entityName),
      "Eye Color": this.safe_val(result.eyeColor),
      Gender: this.safe_val(result.gender),
      "Hair Color": this.safe_val(result.hairColor),
      Height: this.safe_val(result.height),
      Identifier: this.safe_val(result.identifier),
      "Individual Name": this.safe_val(result.individualName),
      Nationality: this.safe_val(result.nationality),
      "Place of Birth": this.safe_val(result.placeOfBirth),
      Program: this.safe_val(result.program),
      Race: this.safe_val(result.race),
      Remarks: this.safe_val(result.remarks),
      "Source Agency Acronym": this.safe_val(result.sourceAgencyAcronym),
      "Source Agency Name": this.safe_val(result.sourceAgencyName),
      "Source List Type": this.safe_val(result.sourceListType),
      "Source Parent Agency": this.safe_val(result.sourceParentAgency),
      "Source Region": this.safe_val(result.sourceRegion),
      Weight: this.safe_val(result.weight),
      "WLS Result Type": this.safe_val(result.wlsResultType),
    }));
  }

  /**
   * Format a date string like "19280318" into "1928-03-18".
   */
  format_date_string(date_str) {
    if (!date_str || date_str === "N/A") return "N/A";
    if (typeof date_str === "string" && /^\d{8}$/.test(date_str)) {
      return `${date_str.slice(0, 4)}-${date_str.slice(4, 6)}-${date_str.slice(
        6,
        8,
      )}`;
    }
    return date_str;
  }

  /**
   * Prepare raw JSON data
   */
  prepare_raw_json_data() {
    return {
      ResponseInfo: this.response_info,
      RequestData: this.request_data,
      DSWatchlistVerification: this.aml_data,
      DRWatchlistVerification: this.watchlist_summary,
      VerificationStatus: this.verification_status,
    };
  }
}
